import React, { useState } from "react";
import { Box, Button, Link, Card as MuiCard } from "@alpha/ui-lib/ui/external";
import { makeStyles } from "@alpha/ui-lib/theme";
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { faArrowRight, faChevronDown, faChevronUp, faDownload } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from "@alpha/ui-lib/ui/Typography";
import { ActionButton } from "@alpha/ui-lib/ui/button/ActionButton";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { CurrencyAccountDto } from '@alpha/currency-accounts-dtos';
import useReportsPolling, { DownloadType, FileType, PageType } from 'hooks/useReportsPolling';
import { useTranslation } from "react-i18next";

const Card = MuiCard as React.FC<React.ComponentProps<typeof MuiCard>>;

export const useStyles = makeStyles(
  () => ({
    accountInformation: {
      padding: '16px',
      overflow: 'hidden',
      minWidth: 0,
    },
    accountInformationList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      margin: '16px 0px'
    },
    accountInformationItem: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      border: '1px solid #EAEAEA',
      borderRadius: '5px',
      padding: '16px',
    },
    accountInformationContent: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    downloadButton: {
      backgroundColor: '#1E87771A',
      color: '#1E8777',
      '&:hover': {
        backgroundColor: '#1E8777',
        color: '#FFFFFF',
      },
    },
    collapsibleSection: {
      overflow: 'hidden',
      transition: 'max-height 0.5s ease-in-out',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    },
    viewAllButtonContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#1E8777',
      cursor: 'pointer',
      textDecoration: 'underline',
      marginTop: '16px',
      textAlign: 'center',
    },
    }),
    { name: 'AccountInformation' },
  );

interface IProps {
  currencyAccounts: CurrencyAccountDto[];
}

const AccountInformation: React.FC<IProps> = ({ currencyAccounts }) => {
  const [viewAll, setViewAll] = useState(false);
  
  const { t } = useTranslation();
  const classes = useStyles();
  const { executeReportGeneration } = useReportsPolling(PageType.CurrencyAccount);

  const corpayRequiredFields = (account: CurrencyAccountDto) => {
    return account.corpayAccountIdentifier && account.corpayAccountCurrency;
  };

  const handleDownload = (accountId: string) => {
    executeReportGeneration(FileType.PDF, accountId, undefined, undefined, DownloadType.AccountLetter, FileType.PDF, undefined, undefined, undefined, undefined, true);
  };

  const accountInformation = currencyAccounts
    .filter((account) => corpayRequiredFields(account))
    .map((account) => ({
      id: account.id,
      title: `${account.corpayAccountCurrency} - ${account.corpayAccountIdentifier}`,
      description: null,
    }));

  const handleViewAll = () => {
    setViewAll(!viewAll);
  }

  return (
    <>
    <Card className={classes.accountInformation}>
      <Typography variant="h3">{t('corpay_account_information')}</Typography>
      <div>Download the bank details for your Corpay settlement accounts below. These are the Corpay equivalents of the currency accounts you currently pay into. Multi-Currency Accounts (MCAs) may include multiple supported currencies.</div>
      <Box className={classes.accountInformationList}>
        {accountInformation.slice(0, 5).map((account) => (
          <Box className={classes.accountInformationItem} key={account.id}>
            <Box className={classes.accountInformationContent}>
              <Typography variant="body1">{account.title}</Typography>
              {account.description && <Typography variant="body1" style={{ color: '#00000099' }}>{account.description}</Typography>}
            </Box>
            <ActionButton rounded onClick={() => handleDownload(account.id)} className={classes.downloadButton} startIcon={<FontAwesomeIcon icon={faDownload as IconProp} />}>{t('download')}</ActionButton>
          </Box>
        ))}
        <Box className={classes.collapsibleSection} style={{ maxHeight: viewAll ? '1000px' : '0px' }}>
          {accountInformation.slice(5).map((account) => (
            <Box className={classes.accountInformationItem} key={account.id}>
              <Box className={classes.accountInformationContent}>
                <Typography variant="body1">{account.title}</Typography>
                {account.description && <Typography variant="body2">{account.description}</Typography>}
              </Box>
              <ActionButton rounded onClick={() => handleDownload(account.id)} className={classes.downloadButton} startIcon={<FontAwesomeIcon icon={faDownload as IconProp} />}>{t('download')}</ActionButton>
            </Box>
          ))}
        </Box>
          {accountInformation.length > 5 && (
            <Box className={classes.viewAllButtonContainer}>
              <Typography variant="body1" onClick={handleViewAll} style={{ marginRight: '8px' }}>{viewAll ? t('collapse') : `(+${accountInformation.length - 5}) ${t('view_all')}`}</Typography>
              <FontAwesomeIcon icon={viewAll ? faChevronUp as IconProp : faChevronDown as IconProp} />
            </Box>
          )}
      </Box>
    </Card>
    </>
  )
}

export default AccountInformation;